import axios from "axios";
import { useEffect, useState } from "react";
import { getApiUrl } from "src/environment";
import { getApiAuthData, getDateTimeRecord } from "src/utils";

// TODO(BE): turn on once "get-field-history" exists.
export const FIELD_HISTORY_ENABLED = false;

export interface IFieldHistoryParams {
  lbid: string;
  lbeid: string;
  sectionIndex: number;
  subSectionIndex?: number;
  rowIndex: number;
  subRowIndex?: number;
  colIndex: number;
}

export interface IFieldHistoryPoint {
  time: number;
  value: number;
  lbeid: string;
}

const paramsKey = (params: IFieldHistoryParams) =>
  JSON.stringify([
    params.lbid,
    params.lbeid,
    params.sectionIndex,
    params.subSectionIndex ?? null,
    params.rowIndex,
    params.subRowIndex ?? null,
    params.colIndex,
  ]);

// Expected response: { points: IFieldHistoryPoint[] }, earlier entries only, oldest first.
export const fetchFieldHistory = async (
  params: IFieldHistoryParams,
): Promise<IFieldHistoryPoint[]> => {
  const res = await axios.post(
    getApiUrl(),
    JSON.stringify({
      request: "get-field-history",
      _app: true,
      ...getApiAuthData(),
      ...params,
      dto: getDateTimeRecord(),
    }),
  );
  return res.data?.points ?? [];
};

const cache = new Map<string, Promise<IFieldHistoryPoint[]>>();

const getCachedHistory = (params: IFieldHistoryParams) => {
  const key = paramsKey(params);
  let promise = cache.get(key);
  if (!promise) {
    promise = fetchFieldHistory(params);
    cache.set(key, promise);
    promise.catch(() => {
      if (cache.get(key) === promise) cache.delete(key);
    });
  }
  return promise;
};

const EMPTY: IFieldHistoryPoint[] = [];

const useFieldHistory = (
  params: IFieldHistoryParams | null,
  enabled: boolean,
) => {
  const [points, setPoints] = useState(EMPTY);
  const key = params ? paramsKey(params) : null;

  useEffect(() => {
    if (!enabled || !params) return;
    let cancelled = false;
    getCachedHistory(params)
      .then(result => {
        if (!cancelled) setPoints(result);
      })
      .catch(() => {
        if (!cancelled) setPoints(EMPTY);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key]);

  return { points };
};

export default useFieldHistory;
